import { Customer, useRestApiProvider } from "@jugl-web/rest-api";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useCustomerFields } from "./useCustomerFields";

interface UseCustomersOptions {
  entityId: string;
  searchQuery?: string;
  skipInitialLoading?: boolean;
  searchBy?: string[];
}

interface CustomerListState {
  list: Customer[];
  currentPage: number;
  totalPages: number;
  isSearch?: boolean;
}

const DEFAULT_PAGE_SIZE = 20;

export const useCustomers = ({
  entityId,
  searchQuery,
  skipInitialLoading,
  searchBy,
}: UseCustomersOptions) => {
  const [listState, setListState] = useState<CustomerListState>({
    list: [],
    currentPage: 1,
    totalPages: 0,
    isSearch: false,
  });

  const [isLoading, setIsLoading] = useState(false);

  const { customersApi } = useRestApiProvider();
  const { formFields, getFieldDefinition } = useCustomerFields({ entityId });
  const transformedSearchBy = useMemo(() => {
    if (!searchQuery) {
      return undefined;
    }
    const firstNameField = formFields.find(
      (field) => field.name === "first_name" && field.section === "personal"
    );

    if (!formFields || !searchBy || searchBy.length === 0 || !firstNameField) {
      return undefined;
    }

    const result = [
      {
        id: firstNameField.id,
        values: null,
      },
      ...searchBy.map((fieldId) => {
        const fieldDef = getFieldDefinition(fieldId);
        if (!fieldDef || fieldDef.name === "first_name") {
          return undefined;
        }
        if (fieldDef.type === "dropdown") {
          return {
            id: fieldDef.id,
            values: fieldDef.values,
          };
        }
        return {
          id: fieldDef.id,
          values: null,
        };
      }),
    ].filter(
      (
        item
      ): item is {
        id: string;
        values:
          | { id: string; value: string; color: string; order: number }[]
          | null;
      } => item !== undefined
    );

    return JSON.stringify(result);
  }, [formFields, getFieldDefinition, searchBy, searchQuery]);

  const { data: firstPageData, refetch: refetchFirstPage } =
    customersApi.useCustomersListQuery(
      {
        entityId,
        params: {
          page: 1,
          page_size: DEFAULT_PAGE_SIZE,
          search_term: searchQuery || undefined,
          search_by: transformedSearchBy,
        },
      },
      {
        skip: skipInitialLoading,
      }
    );

  useEffect(() => {
    if (firstPageData) {
      setListState(() => ({
        list: firstPageData.data,
        currentPage: firstPageData.page_number,
        totalPages: firstPageData.total_pages,
        isSearch: !!searchQuery,
      }));
    }
  }, [firstPageData, searchQuery]);

  const [fetchCustomers] = customersApi.useLazyCustomersListQuery();
  const lastRequestRef = useRef<ReturnType<typeof fetchCustomers> | null>(null);

  const loadCustomers = useCallback(
    async (page = 1) => {
      if (page === 1) {
        return;
      }

      setIsLoading(true);

      lastRequestRef.current?.abort();

      const request = fetchCustomers(
        {
          entityId,
          params: {
            page,
            page_size: DEFAULT_PAGE_SIZE,
            search_term: searchQuery || undefined,
          },
        },
        true
      );

      lastRequestRef.current = request;

      const response = await request;

      if (response.status === "fulfilled") {
        const fetchedCustomers = response.data.data;

        setListState((previousState) => ({
          list: [...previousState.list, ...fetchedCustomers],
          currentPage: response.data.page_number,
          totalPages: response.data.total_pages,
          isSearch: !!searchQuery,
        }));
      }

      setIsLoading(false);
    },
    [entityId, fetchCustomers, searchQuery]
  );

  const reachedEnd = listState.currentPage >= listState.totalPages;

  const loadMore = useCallback(() => {
    if (isLoading || reachedEnd) {
      return;
    }

    loadCustomers(listState.currentPage + 1);
  }, [isLoading, reachedEnd, loadCustomers, listState]);

  const setList = useCallback(
    (
      valueOrCallback: Customer[] | ((previousList: Customer[]) => Customer[])
    ) => {
      setListState((previousListState) => ({
        ...previousListState,
        list:
          typeof valueOrCallback === "function"
            ? valueOrCallback(previousListState.list)
            : valueOrCallback,
      }));
    },
    []
  );

  const refresh = useCallback(async () => {
    setIsLoading(true);
    lastRequestRef.current?.abort();
    await refetchFirstPage();
    setIsLoading(false);
  }, [refetchFirstPage]);

  return {
    customers: listState.list,
    listState,
    isLoading,
    reachedEnd,
    loadMore,
    setList,
    refresh,
  };
};
